import graphql from 'babel-plugin-relay/macro';
import React from 'react';
import {usePaginationFragment, useMutation} from 'react-relay/hooks';
import ConnectionHandler from 'relay-connection-handler-plus';
import useFilters from 'src/hooks/useFilters';
import {showEventNotification} from 'src/utils/notifications';

import Table from '../../../components/table';
import DateCell from '../../../components/table/cell-types/DateCell';
import SecondaryTextCell from '../../../components/table/cell-types/SecondaryTextCell';
import {AssetFamiliesOrder} from './__generated__/GeneralAssetFamiliesPaginationQuery.graphql';
import {GeneralAssetFamiliesTable_generalAssetFamilies$key} from './__generated__/GeneralAssetFamiliesTable_generalAssetFamilies.graphql';
import {GeneralAssetFamiliesTableDeleteGeneralAssetFamilyMutation} from './__generated__/GeneralAssetFamiliesTableDeleteGeneralAssetFamilyMutation.graphql';
import {listPageGeneralAssetFamiliesQuery} from './__generated__/listPageGeneralAssetFamiliesQuery.graphql';
import TableFilters from './TableFilters';

type Props = {
  generalAssetFamilies: GeneralAssetFamiliesTable_generalAssetFamilies$key;
};

type Filters = {
  name?: string;
  orderBy?: AssetFamiliesOrder;
};

function GeneralAssetFamiliesTable(props: Props) {
  const {data, loadNext, hasNext, refetch} = usePaginationFragment<
    listPageGeneralAssetFamiliesQuery,
    GeneralAssetFamiliesTable_generalAssetFamilies$key
  >(
    graphql`
      fragment GeneralAssetFamiliesTable_generalAssetFamilies on Query
        @argumentDefinitions(
          first: {type: "Int!"}
          after: {type: "String"}
          name: {type: "String"}
          orderBy: {type: "AssetFamiliesOrder"}
        )
        @refetchable(queryName: "GeneralAssetFamiliesPaginationQuery") {
        generalAssetFamilies(first: $first, after: $after, orderBy: $orderBy, name: $name)
          @connection(key: "GeneralAssetFamiliesList_generalAssetFamilies") {
          totalCount
          pageInfo {
            hasPreviousPage
            hasNextPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              createdAt
              name
              manufacturer {
                id
                name
              }
            }
          }
        }
      }
    `,
    props.generalAssetFamilies,
  );

  const [commit] = useMutation<GeneralAssetFamiliesTableDeleteGeneralAssetFamilyMutation>(
    deleteGeneralAssetFamilyMutation,
  );

  const {addFilters, setFilters, isTransitioning} = useFilters<Filters>({}, refetch);

  return (
    <Table
      filters={() => (
        <TableFilters onApplyFilters={handleOnApplyFilter} onResetFilters={handleOnResetFilter} />
      )}
      actions={{show: false, delete: true, edit: true, create: true}}
      totalDataLength={data.generalAssetFamilies.totalCount}
      columns={columns}
      isTransitioning={isTransitioning}
      onPaginate={() => hasNext && loadNext(50)}
      onSort={handleOnSort}
      onDelete={handleDelete}
      data={data.generalAssetFamilies.edges}
    />
  );

  function handleOnApplyFilter(filters: {text: string}) {
    addFilters({name: filters.text});
  }

  function handleOnResetFilter() {
    setFilters({});
  }

  async function handleOnSort(sortByConfig: any[]) {
    if (!sortByConfig.length) {
      addFilters({orderBy: undefined});

      return;
    }

    const orderByDirection = sortByConfig[0].desc ? 'DESC' : 'ASC';
    const orderByField = sortByConfig[0].id.toUpperCase();

    addFilters({orderBy: {direction: orderByDirection, field: orderByField}});
  }

  function handleDelete(id: string) {
    commit({
      variables: {input: {generalAssetFamilyId: id}},
      onCompleted: payload => {
        const entry = payload.deleteGeneralAssetFamily?.generalAssetFamily;

        if (entry) {
          showEventNotification('removed', 'Type', entry.name);
        }
      },
      updater: store => {
        const root = store.getRoot();

        const connections = ConnectionHandler.getConnections(
          root,
          'GeneralAssetFamiliesList_generalAssetFamilies',
        );

        if (!connections) {
          return;
        }

        connections.forEach(connection => {
          ConnectionHandler.deleteNode(connection, id);
        });
      },
    });
  }
}

export default GeneralAssetFamiliesTable;

const deleteGeneralAssetFamilyMutation = graphql`
  mutation GeneralAssetFamiliesTableDeleteGeneralAssetFamilyMutation(
    $input: DeleteGeneralAssetFamilyInput!
  ) {
    deleteGeneralAssetFamily(input: $input) {
      generalAssetFamily {
        id
        name
      }
      errors {
        code
        message
      }
    }
  }
`;

const columns = [
  {
    Header: 'Name',
    accessor: 'node.name',
    id: 'name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
  {
    Header: 'Created On',
    accessor: 'node.createdAt',
    id: 'created_at',
    Cell({cell: {value}}: any) {
      return <DateCell value={value} />;
    },
  },
  {
    Header: 'Manufacturer',
    accessor: 'node.manufacturer.name',
    id: 'manufacturer_name',
    Cell({cell: {value}}: any) {
      return <SecondaryTextCell value={value} />;
    },
  },
];
